import { notification, Tooltip, Modal, Spin } from "antd";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";
import FILE from "../../assets/image/icon-file01.png";
import HTML_1 from "../../assets/image/icon-html03.png";
import PDF_1 from "../../assets/image/icon-pdf01.png";
import XLS_1 from "../../assets/image/icon-xls01.png";
import { endpoints } from "../../services/endpoint.ts/endpoint";
// import { getDetail, getHtmlView, getPdfSummary } from "../../store/summary/action";
import { getDetail } from "../../store/summary/action";
import { darkPalette, keyMapping, URL, UtilRenderColor } from "../../utils/contact";
import { convertSize, filterVal, formatTimestampToDate, getFormattedDate2 } from '../../utils/convertUtil';
import DiskUsage from "./disk/DiskUsage";
import PartitionInfo from "./disk/PartitionInfo";
import NetworkErrTraffic from "./network/NetworkErrTraffic";
import NetworkIP from "./network/NetworkIP";
import NetworkTraffic from "./network/NetworkTraffic";
import RoutingPage from "./routing/route";
import StabilityInfo from "./stability/StabilityInfo";
import "./SummaryStyled.scss";

const Summary = () => {
  const { t } = useTranslation();

  const [detailSummary, setDetailSummary] = useState({});
  const [uploadDate, setUploadDate] = useState('');
  const [extractedDate, setExtractedDate] = useState('');
  const [randomColors, setRandomColors] = useState({});
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // 다양한 표시 모드 확인
  const isPopupWindow = window.opener && window.opener !== window;
  const isIframeView = window.self !== window.top;
  const isStandalone = !isPopupWindow && !isIframeView;

  useEffect(() => {
    // iframe 내에서 실행 중인지 확인
    const isIframeView = window.self !== window.top;
    
    // 독립 실행 모드인지 확인
    const isStandalone = !isIframeView && !isPopupWindow;
    
    // 독립 실행 모드일 때만 문서 제목 변경
    if (isStandalone) {
      document.title = `GROWIN - Summary`;
    } else if (isPopupWindow) {
      document.title = `GROWIN - Summary`;
    }
    
    // iframe 내에서 실행 중이면 body 클래스 추가
    if (isIframeView) {
      document.body.classList.add('iframe-view');
      
      // iframe 내부에 스크롤바 스타일 추가
      const style = document.createElement('style');
      style.textContent = `
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
          display: block;
        }
        
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        
        ::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.4) !important;
        }
        
        ::-webkit-scrollbar-thumb:active {
          background-color: rgba(0, 0, 0, 0.6) !important;
        }
        
        ::-webkit-scrollbar-button {
          display: none !important;
          height: 0 !important;
          width: 0 !important;
        }
        
        * {
          scrollbar-width: thin;
        }
        
        *:hover::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.3);
        }
      `;
      style.setAttribute('data-scrollbar-style', 'true');
      document.head.appendChild(style);
    }
    
    return () => {
      // 컴포넌트 언마운트 시 클래스 제거
      document.body.classList.remove('iframe-view');
      
      // 스타일 요소 제거 (필요한 경우)
      if (isIframeView) {
        const styleElement = document.querySelector('style[data-scrollbar-style]');
        if (styleElement) {
          styleElement.remove();
        }
      }
    };
  }, [id, isPopupWindow]);

  useEffect(() => {
    // 페이지 로드 시 실행
    getDetailSummary(id);
    
    // 랜덤 색상 생성
    const colors = {};
    Object.keys(keyMapping).forEach((key) => {
      const randomIndex = Math.floor(Math.random() * darkPalette.length);
      colors[key] = darkPalette[randomIndex];
    });
    setRandomColors(colors);
    
  }, [id, isStandalone]);

  useEffect(() => {
    // PDF 생성을 위한 스타일 추가
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        .summary-wapper__down {
          page-break-before: always;
          margin-top: 20px;
        }
        
        .summary-wapper__down:first-of-type {
          page-break-before: avoid;
        }
        
        .summary-header, .summary-wapper__title {
          page-break-after: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const diskUsage = useMemo(() => {
    const disk = filterVal('diskUsage', detailSummary)[0] || '';
    const dlines = disk.trim().split('\n');
    const dheaders = dlines[0]?.split(/\s+/) || [];

    return dlines.slice(1).map(dline => {
      const columns = dline.split(/\s+/);
      const entry = {};
      dheaders.forEach((header, index) => {
        entry[header] = isNaN(columns[index]) ? columns[index] : convertSize(parseInt(columns[index]));
      });
      return entry;
    });
  }, [detailSummary]);

  const getFilename = filterVal('filename', detailSummary);
  const part = filterVal('partitionInfo', detailSummary);
  const networkIP = filterVal('networkIP', detailSummary);
  const network_traffic = filterVal('networkTraffic', detailSummary);
  const ntlines = (network_traffic+"").trim().split('\n');
  const ntheaders = ntlines[0].split(/\s+/).slice(1);
  const network_err_traffic = filterVal('networkErrTraffic', detailSummary);
  const netlines = (network_err_traffic+"").trim().split('\n');
  const netheaders = netlines[0].split(/\s+/).slice(1);

  const getDetailSummary = async (id) => {
    try {
      // console.log("Summary - Fetching detail for id:", id);
      const response = await getDetail(id);
      // console.log("Summary - Detail response:", response);
      if (response) {
        delete response.id;
        let packageCount = 'N/A';
        if (response.packageList) {
          const plist = response.packageList;
          packageCount = plist === 'N/A' ? 'N/A' : plist.split('\n').filter(Boolean).length;
        }
        
        let insDate = 'N/A';
        if (response.insDate) {
          insDate = formatTimestampToDate(response.insDate);
        }
        setUploadDate(insDate)
        let exDate = 'N/A';
        if (response.extractedDate) {
          exDate = response.extractedDate;
        }
        setExtractedDate(exDate);

        setDetailSummary({ ...response, packageCount });
        // console.log("Summary - Detail summary set:", { ...response, packageCount });
      } else {
        console.warn("Summary - No response received");
      }
    } catch (error) {
      console.error("Summary - Error fetching detail:", error);
    } finally {
      setPageLoading(false);
    }
  };

  const renderTooltipContent = (message) => {
    if (!message) return null;
    
    // 메시지를 줄바꿈으로 분리
    const lines = message.split('\n');
    
    // 섹션 링크 매핑
    const sectionLinks = {
      /*
      Disk Usage Check    disk-usage-section
      Disk Mount Check    stability-section
      Swap Memory Check   summary-info-section
      Memory Usage Check  summary-info-section
      Network Link Check  stability-section
      Network Bond Check  stability-section
      Time Sync Check     stability-section
      Cpu Usage Check     summary-info-section
      Multipath Check     stability-section
      Route Check         stability-section
       */
      'Disk Usage Check' : 'disk-usage-section',
      'Disk Mount Check': 'stability-section',
      'Swap Memory Check': 'summary-info-section',
      'Memory Usage Check': 'summary-info-section',
      'Network Link Check': 'stability-section',
      'Network Bond Check': 'stability-section',
      'Time Sync Check': 'stability-section',
      'Cpu Usage Check': 'summary-info-section',
      'Multipath Check': 'stability-section',
      'Route Check': 'stability-section'
    };
    
    return (
      <div style={{ color: 'white' }}>
        {lines.map((line, index) => {
          // 각 라인에서 섹션 키워드 확인
          const matchedSection = Object.keys(sectionLinks).find(keyword => 
            line.includes(keyword)
          );
          
          if (matchedSection) {
            // 키워드가 포함된 라인에 링크 추가
            return (
              <div key={index} style={{ marginBottom: '5px' }}>
                <a 
                  href={`#${sectionLinks[matchedSection]}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const sectionElement = document.getElementById(sectionLinks[matchedSection]);
                    if (sectionElement) {
                      sectionElement.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  style={{ 
                    color: 'white',
                    textDecoration: 'none',
                    borderBottom: '1px dashed rgba(255, 255, 255, 0.7)',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    padding: '2px 0',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderBottom = '1px solid white';
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderBottom = '1px dashed rgba(255, 255, 255, 0.7)';
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  {line} <span style={{ marginLeft: '4px', fontSize: '10px' }}>↓</span>
                </a>
              </div>
            );
          }
          
          // 일반 텍스트 라인
          return <div key={index} style={{ marginBottom: '5px' }}>{line}</div>;
        })}
      </div>
    );
  };

  // 라우팅 섹션 처리를 위한 별도 함수
  const handleRoutingSection = async (section, pdfDoc, currentY, pageWidth, pageHeight, marginLeft, marginTop) => {
    console.log('라우팅 섹션 처리를 시작합니다.');
    
    try {
      // 페이지에 충분한 공간이 있는지 확인 (최소 200mm 필요)
      const requiredSpace = 200; // 라우팅 섹션에 필요한 최소 공간 (예상치)
      if (currentY + requiredSpace > pageHeight - marginTop) {
        // 새 페이지 추가
        pdfDoc.addPage();
        currentY = marginTop;
      }
      
      // 메인 제목 배경 (어두운 회색으로 수정)
      const titleHeight = 20;
      
      // A4 기준 페이지 너비 (mm)
      const totalPageWidth = 210; // A4 기준
      
      // 실제 콘텐츠 너비 (하드코딩) - 수동 조절 가능
      const titleWidth = 185; // 타이틀 너비 - 필요에 따라 조정 (예: 150-180mm)
      
      // 중앙 정렬을 위한 왼쪽 여백 계산
      const titleStartX = (totalPageWidth - titleWidth) / 2;
      
      // 배경 사각형 그리기
      pdfDoc.setFillColor(255, 255, 255);
      pdfDoc.rect(titleStartX, currentY, titleWidth, titleHeight, 'F');
      
      // 메인 제목 텍스트 (흰색, 가운데 정렬)
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.setTextColor(30, 35, 50);
      pdfDoc.text('Routing Information', titleStartX + (titleWidth / 2), currentY + titleHeight/2 + 6, { align: 'center' });
      
      currentY += titleHeight + 5; // 여백 증가하여 다른 섹션과 일관되게
      
      // 라우팅 섹션 요소 찾기
      const routeContainer = section.querySelector('.route_route_container__2Wjw5') || 
                            section.querySelector('[class*="route_container"]') || 
                            section.querySelector('[class*="route_route_container"]');
      
      if (!routeContainer) {
        console.log('라우팅 컨테이너를 찾을 수 없습니다.');
        return currentY;
      }
      
      // 라우팅 섹션 요소들 찾기
      const routeSections = routeContainer.querySelectorAll('div[class*="route_section"]') || 
                            routeContainer.children;
      
      // 각 라우팅 섹션 처리
      for (let i = 0; i < routeSections.length; i++) {
        const routeSection = routeSections[i];
        
        // 섹션 제목 찾기
        const titleElement = routeSection.querySelector('h3, h4') || 
                              routeSection.querySelector('[class*="title"]');
        
        let sectionTitle = '';
        if (titleElement) {
          sectionTitle = titleElement.textContent.trim();
        }
        
        // 필요한 타이틀인지 확인 (Default Routing, Network Routing 등)
        if (!['Default Routing', 'Network Routing', 'Local Routing', 
              'Broadcast Routing', 'IPv6 Routing'].includes(sectionTitle)) {
          continue; // 관련 없는 섹션은 건너뛰기
        }
        
        // 테이블 찾기
        const table = routeSection.querySelector('table');
        if (!table) continue;
        
        // 행 찾기
        const rows = table.querySelectorAll('tr');
        if (rows.length < 2) continue; // 헤더 + 최소 1개 데이터 행 필요
        
        // 새 페이지 확인
        if (currentY + 60 > pageHeight - marginTop) {
          pdfDoc.addPage();
          currentY = marginTop;
        }
        
        // 서브타이틀(Default Routing 등) 글자 크기 조절
        pdfDoc.setFontSize(9);
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.setTextColor(0, 0, 0);
        
        // 서브타이틀도 메인 타이틀과 같은 위치에 배치
        const subtitleStartX = (totalPageWidth - titleWidth) / 2;
        pdfDoc.text(sectionTitle, subtitleStartX, currentY);
        currentY += 2; // 서브타이틀과 테이블 사이 간격 줄임 (15mm → 8mm)
        
        // 테이블 설정 - 메인 타이틀과 동일한 크기로 설정
        const tableWidth = titleWidth;
        
        // 헤더 행 처리
        const headerCells = rows[0].querySelectorAll('th, td');
        const headers = Array.from(headerCells).map(cell => cell.textContent.trim());
        
        // 컬럼 너비 계산
        const columnWidths = [];
        if (headers.length === 4) { // 일반적인 경우
          // Network, Gateway, Interface, Flags에 맞게 최적화된 비율
          columnWidths.push(tableWidth * 0.25); // Network
          columnWidths.push(tableWidth * 0.25); // Gateway
          columnWidths.push(tableWidth * 0.2);  // Interface
          columnWidths.push(tableWidth * 0.3);  // Flags
        } else {
          // 균등 분배
          const width = tableWidth / headers.length;
          for (let i = 0; i < headers.length; i++) {
            columnWidths.push(width);
          }
        }
        
        // 헤더 스타일 - 메인 타이틀과 동일한 위치에 그리기
        const headerHeight = 6; // 헤더 높이 줄임 (18mm → 6mm)
        pdfDoc.setFillColor(30, 35, 50); // 헤더 배경색 변경
        pdfDoc.rect(subtitleStartX, currentY, tableWidth, headerHeight, 'F');
        
        // 헤더 텍스트 - 수직 위치 조정
        let xOffset = subtitleStartX;
        for (let j = 0; j < headers.length; j++) {
          // 헤더 텍스트의 수직 위치 조정 (중앙 정렬)
          pdfDoc.setFontSize(8);
          pdfDoc.setTextColor(255, 255, 255); // 텍스트 색상 흰색으로 변경
          pdfDoc.text(headers[j], xOffset + 8, currentY + headerHeight/2 + 1, { 
            align: 'left',
            baseline: 'middle'  // 텍스트 베이스라인을 중앙으로 설정
          });
          xOffset += columnWidths[j];
        }
        
        currentY += headerHeight;
        
        // 데이터 행 처리
        const rowHeight = 8; // 사용자 지정 행 높이 유지
        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.setFontSize(8); // 사용자 지정 폰트 크기 유지
        pdfDoc.setTextColor(255, 255, 255); // 데이터 텍스트도 흰색으로 변경
        
        for (let j = 1; j < rows.length; j++) {
          // 페이지 넘김 처리
          if (currentY + rowHeight > pageHeight - marginTop) {
            pdfDoc.addPage();
            currentY = marginTop;
            
            // 헤더 다시 그리기
            pdfDoc.setFillColor(30, 35, 50); // 헤더 배경색 유지
            pdfDoc.rect(subtitleStartX, currentY, tableWidth, headerHeight, 'F');
            
            pdfDoc.setFontSize(8);
            pdfDoc.setFont('helvetica', 'bold');
            pdfDoc.setTextColor(255, 255, 255); // 헤더 텍스트 색상 유지
            
            xOffset = subtitleStartX;
            for (let k = 0; k < headers.length; k++) {
              pdfDoc.text(headers[k], xOffset + 8, currentY + headerHeight/2 + 1, { 
                align: 'left',
                baseline: 'middle'  // 텍스트 베이스라인을 중앙으로 설정
              });
              xOffset += columnWidths[k];
            }
            
            currentY += headerHeight;
            pdfDoc.setFont('helvetica', 'normal');
            pdfDoc.setTextColor(255, 255, 255);
          }
          
          // 행 배경색 (모든 행에 동일한 색상 적용)
          pdfDoc.setFillColor(45, 52, 71); // tbody 배경색 변경
          pdfDoc.rect(subtitleStartX, currentY, tableWidth, rowHeight, 'F');
          
          // 셀 데이터 - 수직 위치 조정
          const cells = rows[j].querySelectorAll('td');
          xOffset = subtitleStartX;
          
          for (let k = 0; k < cells.length && k < headers.length; k++) {
            const cellText = cells[k].textContent.trim();
            // 데이터 텍스트의 수직 위치 조정 (중앙 정렬)
            pdfDoc.text(cellText, xOffset + 8, currentY + rowHeight/2 + 1, { 
              align: 'left',
              baseline: 'middle'  // 텍스트 베이스라인을 중앙으로 설정
            });
            xOffset += columnWidths[k];
          }
          
          currentY += rowHeight;
        }
        
        // 테이블 주변 테두리
        // pdfDoc.setDrawColor(180, 180, 180);
        // pdfDoc.setLineWidth(0.3);
        // pdfDoc.rect(
        //   subtitleStartX, 
        //   currentY - rowHeight * (rows.length - 1) - headerHeight, 
        //   tableWidth, 
        //   rowHeight * (rows.length - 1) + headerHeight, 
        //   'S'
        // );
        
        currentY += 10; // 테이블 사이 여백 줄임 (20mm → 10mm)
      }
      
      return currentY;
    } catch (error) {
      console.error('라우팅 섹션 처리 중 오류:', error);
      return currentY + 10;
    }
  };

  // Partition Information 섹션 처리를 위한 별도 함수
  const handlePartitionSection = async (section, pdfDoc, currentY, pageWidth, pageHeight, marginLeft, marginTop) => {
    try {
      // 페이지에 충분한 공간이 있는지 확인
      if (currentY + 150 > pageHeight - marginTop) {
        pdfDoc.addPage();
        currentY = marginTop;
      }
      
      // 섹션 캡쳐
      const sectionCanvas = await html2canvas(section, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      const sectionImgData = sectionCanvas.toDataURL('image/png');
      const sectionImgProps = pdfDoc.getImageProperties(sectionImgData);
      const sectionImgWidth = pageWidth;
      const sectionImgHeight = (sectionImgProps.height * sectionImgWidth) / sectionImgProps.width;
      
      // 이미지 추가
      pdfDoc.addImage(sectionImgData, 'PNG', marginLeft, currentY, sectionImgWidth, sectionImgHeight);
      currentY += sectionImgHeight + 5;
      
      return currentY;
    } catch (error) {
      console.error('Partition Information 섹션 처리 중 오류:', error);
      return currentY + 10;
    }
  };

  const generatePDF = async () => {
    setLoadingType('pdf');
    setIsLoading(true);
    
    try {
      // PDF 문서 생성 (여백 설정 추가)
      const pdfDoc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
        margins: { top: 10, right: 10, bottom: 10, left: 10 }
      });
      
      const pageWidth = pdfDoc.internal.pageSize.getWidth() - 20; // 좌우 여백 10mm씩
      const pageHeight = pdfDoc.internal.pageSize.getHeight() - 20; // 상하 여백 10mm씩
      const marginLeft = 10;
      const marginTop = 10;
      
      // 첫 페이지에 "Summary Report" 타이틀 추가
      pdfDoc.setFont("helvetica", "bold");
      pdfDoc.setFontSize(22);
      pdfDoc.setTextColor(0, 0, 0);
      pdfDoc.text("Summary Report", pageWidth / 2 + marginLeft, marginTop + 8, { align: "center" });
      
      let currentY = marginTop + 15; // 타이틀 아래부터 시작
      currentY += 5;
      
      // 헤더 영역 캡처 (action-buttons 제외)
      const headerSection = document.querySelector('.summary-header');
      if (!headerSection) throw new Error('헤더 섹션을 찾을 수 없습니다.');
      
      // 헤더 복제 및 action-buttons 제거
      const headerClone = headerSection.cloneNode(true);
      const actionButtons = headerClone.querySelector('.action-buttons');
      if (actionButtons) {
        actionButtons.remove();
      }
      
      // 임시 요소에 헤더 추가
      const tempHeader = document.createElement('div');
      tempHeader.appendChild(headerClone);
      document.body.appendChild(tempHeader);
      
      // 헤더 컨테이너 스타일 설정 - 기졸 레이아웃 유지
      tempHeader.style.width = '100%';
      tempHeader.style.padding = '10px';
      
      // 헤더 내의 모든 텍스트 요소 선택하여 글자 크기 키우기
      const headerTextElements = tempHeader.querySelectorAll('*');
      headerTextElements.forEach(el => {
        // 텍스트 노드를 포함하는 요소만 처리
        if (el.childNodes && Array.from(el.childNodes).some(node => 
            node.nodeType === Node.TEXT_NODE && node.textContent.trim())) {
          // 현재 글꼴 크기 가져오기
          const currentSize = window.getComputedStyle(el).fontSize;
          // 글꼴 크기 1.2배 증가 (더 적은 증가)
          const newSize = parseFloat(currentSize) * 1.5;
          el.style.fontSize = `${newSize}px`;
          el.style.fontWeight = 'bold';
        }
      });
      
      // 헤더 컬럼 간격 조정 - 선택자 수정
      const headerContainer = tempHeader.querySelector('.summary-header');
      const infoSectionElement = headerContainer.querySelector('.info-section');
      const headerColumns = infoSectionElement.querySelectorAll('.info-item');

      // console.log('headerColumns 길이:', headerColumns.length);
      // console.log('headerColumns 배열:', headerColumns);

      if (headerColumns && headerColumns.length > 0) {
        // info-section 스타일 설정
        infoSectionElement.style.display = 'flex';
        infoSectionElement.style.flexWrap = 'nowrap';
        infoSectionElement.style.width = '100%';
        infoSectionElement.style.gap = '20px'; // 컬럼 간 간격 설정
        
        // 첫 번째 컬럼(REPORT NAME) - 더 많은 공간 할당
        if (headerColumns[0]) {
          // console.log('첫 번째 컬럼:', headerColumns[0]);
          // 첫 번째 컬럼에 더 많은 공간 할당 (flex: 6)
          headerColumns[0].style.cssText = "flex: 1; min-width: 500px;";
          
          // 내부 요소들의 너비도 확장
          const infoLabel = headerColumns[0].querySelector('.info-label');
          const infoValue = headerColumns[0].querySelector('.info-value');
          
          if (infoLabel) {
            infoLabel.style.display = 'inline-block';
            infoLabel.style.marginRight = '10px';
          }
          
          if (infoValue) {
            // info-value 요소의 너비를 명시적으로 설정
            infoValue.style.display = 'inline-block';
            infoValue.style.minWidth = '400px';
            infoValue.style.width = 'auto';
            infoValue.style.maxWidth = '500px';
            infoValue.style.overflow = 'visible';
            infoValue.style.textOverflow = 'clip';
            infoValue.style.whiteSpace = 'nowrap'; // 줄바꿈 방지
          }
        }
        
        // 두 번째 컬럼(COLLECTION DATE) - 공간 축소
        if (headerColumns[1]) {
          // console.log('두 번째 컬럼:', headerColumns[1]);
          headerColumns[1].style.cssText = "flex: 1; min-width: 300px;";
          // const forceReflow2 = headerColumns[1].offsetHeight;
          // console.log('forceReflow2:', forceReflow2);
        }
        
        // 세 번째 컬럼(UPLOAD DATE) - 공간 축소
        if (headerColumns[2]) {
          // console.log('세 번째 컬럼:', headerColumns[2]);
          headerColumns[2].style.cssText = "flex: 1; min-width: 300px;";
          // const forceReflow3 = headerColumns[2].offsetHeight;
          // console.log('forceReflow3:', forceReflow3);
        }
      }
      
      // 헤더 컨테이너의 HTML 구조 확인
      // console.log('헤더 컨테이너 HTML:', tempHeader.querySelector('.summary-header').innerHTML);
      
      // 헤더 캡처
      const headerCanvas = await html2canvas(tempHeader, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      // 임시 요소 제거
      document.body.removeChild(tempHeader);
      
      // 타이틀 영역 캡처
      const titleSection = document.querySelector('.summary-wapper__title');
      if (!titleSection) throw new Error('타이틀 섹션을 찾을 수 없습니다.');
      
      // 타이틀 복제 및 여백 추가
      const titleClone = titleSection.cloneNode(true);
      const tempTitle = document.createElement('div');
      tempTitle.appendChild(titleClone);
      tempTitle.style.padding = '15px 0'; // 상하 여백 추가
      document.body.appendChild(tempTitle);
      
      const titleCanvas = await html2canvas(tempTitle, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      // 임시 요소 제거
      document.body.removeChild(tempTitle);
      
      // 모든 섹션 가져오기
      const allSections = document.querySelectorAll('.summary-wapper__down');
      // console.log('모든 섹션:', allSections.length);

      if (allSections.length === 0) throw new Error('요약 정보 섹션을 찾을 수 없습니다.');

      // 첫 번째 섹션(요약 정보)만 캡처 - 원래 크기 유지
      const infoSection = allSections[0];
      const infoCanvas = await html2canvas(infoSection, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });
      
      // 첫 페이지에 헤더, 타이틀, 요약 정보 추가
      const headerImgData = headerCanvas.toDataURL('image/png');
      const headerImgProps = pdfDoc.getImageProperties(headerImgData);
      const headerImgWidth = pageWidth;
      const headerImgHeight = (headerImgProps.height * headerImgWidth) / headerImgProps.width;
      
      const titleImgData = titleCanvas.toDataURL('image/png');
      const titleImgProps = pdfDoc.getImageProperties(titleImgData);
      const titleImgWidth = pageWidth;
      const titleImgHeight = (titleImgProps.height * titleImgWidth) / titleImgProps.width;
      
      const infoImgData = infoCanvas.toDataURL('image/png');
      const infoImgProps = pdfDoc.getImageProperties(infoImgData);
      const infoImgWidth = pageWidth;
      const infoImgHeight = (infoImgProps.height * infoImgWidth) / infoImgProps.width;
      
      // 첫 페이지에 헤더 추가
      pdfDoc.addImage(headerImgData, 'PNG', marginLeft, currentY, headerImgWidth, headerImgHeight);
      currentY += headerImgHeight + 5;
      
      // 타이틀 추가
      pdfDoc.addImage(titleImgData, 'PNG', marginLeft, currentY, titleImgWidth, titleImgHeight);
      currentY += titleImgHeight + 3; // 타이틀 아래 여백
      
      // 요약 정보 추가 (한 번만)
      pdfDoc.addImage(infoImgData, 'PNG', marginLeft, currentY, infoImgWidth, infoImgHeight);
      currentY += infoImgHeight + 5;
      
      // 나머지 섹션들 처리 (디스크 사용량, 파티션 정보, 네트워크 IP 등)
      for (let i = 1; i < allSections.length; i++) {
        const section = allSections[i];
        // console.log(`처리 중인 섹션 ${i}:`, section.className);
        
        // 라우팅 정보 섹션인지 확인
        const routeContainer = 
          section.querySelector('.route_route_container__2Wjw5') || 
          section.querySelector('[class*="route_container"]') || 
          section.querySelector('[class*="route_route_container"]');
        
        // Partition Information 섹션인지 확인 - 타이틀 텍스트 기반
        const partitionTitle = section.querySelector('h3, h4, [class*="title"]');
        const isPartitionSection = partitionTitle && 
                                  partitionTitle.textContent.includes('Partition Information');
        
        if (routeContainer) {
          console.log('라우팅 정보 섹션을 감지했습니다. 별도 함수로 처리합니다.');
          // 여기서 handleRoutingSection 함수 호출
          currentY = await handleRoutingSection(
            section, 
            pdfDoc, 
            currentY, 
            pageWidth, 
            pageHeight, 
            marginLeft, 
            marginTop
          );
        }
        // Partition Information 섹션이면 특별히 처리
        else if (isPartitionSection) {
          console.log('Partition Information 섹션을 감지했습니다. 별도 함수로 처리합니다.');
          currentY = await handlePartitionSection(
            section, 
            pdfDoc, 
            currentY, 
            pageWidth, 
            pageHeight, 
            marginLeft, 
            marginTop
          );
        }
        else {
          // 일반 섹션 처리 (라우팅 정보나 파티션 정보가 아닌 경우)
          const sectionCanvas = await html2canvas(section, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
          });
          
          const sectionImgData = sectionCanvas.toDataURL('image/png');
          const sectionImgProps = pdfDoc.getImageProperties(sectionImgData);
          const sectionImgWidth = pageWidth;
          const sectionImgHeight = (sectionImgProps.height * sectionImgWidth) / sectionImgProps.width;
          
          // 현재 페이지에 섹션이 들어갈 수 있는지 확인
          if (currentY + sectionImgHeight > pageHeight) {
            // 새 페이지에 섹션 추가
            pdfDoc.addPage();
            currentY = marginTop;
          }
          
          pdfDoc.addImage(sectionImgData, 'PNG', marginLeft, currentY, sectionImgWidth, sectionImgHeight);
          currentY += sectionImgHeight + 5;
        }
      }
      
      // 모든 섹션 처리 후 푸터 추가
      addFooterToAllPages(pdfDoc);

      // PDF 저장
      const filename = `${getFilename}-${getFormattedDate2()}.pdf`;
      pdfDoc.save(filename);
      
    } catch (error) {
      console.error('PDF 생성 에러:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      
      notification.error({
        message: t('summary.msg.pdfFailTitle'),
        description: t('summary.msg.pdfFailDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // PDF 문서에 푸터 추가하는 함수
  const addFooterToAllPages = (pdfDoc) => {
    const pageCount = pdfDoc.internal.getNumberOfPages();
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const footerText = 'Copyright Growin 2025. All Rights Reserved.';
    
    // 모든 페이지에 푸터 추가
    for (let i = 1; i <= pageCount; i++) {
      pdfDoc.setPage(i);
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(100, 100, 100); // 회색으로 설정
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.text(footerText, pageWidth / 2, pdfDoc.internal.pageSize.getHeight() - 5, { align: 'center' });
    }
    
    return pdfDoc;
  };

  const getExcel = async () => {
    setLoadingType('excel');
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      window.location.href = `${URL}${endpoints.GET_EXCEL_SUMMARY.url}?ids=${id}`;
    } catch (error) {
      notification.error({
        message: t('summary.msg.downloadFailTitle'),
        description: t('summary.msg.excelFailDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const getHTML = async () => {
    // console.log('getHTML 함수 호출됨');
    setLoadingType('html');
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Nginx 프록시를 통한 HTML 뷰어 호출
      const htmlBasePath = process.env.REACT_APP_HTML_PATH;
      
      // 파일 경로 생성
      const filePath = `${htmlBasePath}${detailSummary.username}/${detailSummary.orgFilename}`;
      
      // Nginx 프록시 경로 사용 (/html-viewer/로 시작하면 nginx가 백엔드로 프록시)
      const fullUrl = `/html-viewer/upload?file_path=${encodeURIComponent(filePath)}`;
      
      console.log('HTML 뷰어 URL:', fullUrl);
      
      // 새 창으로 열기
      const newWindow = window.open(fullUrl, '_blank');
      
      // 팝업 차단 확인
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        notification.warning({
          message: t('summary.msg.popupBlockedTitle'),
          description: t('summary.msg.popupBlockedDesc'),
          placement: 'top',
          duration: 3,
        });
      }
      
    } catch (error) {
      console.error('HTML viewer error:', error);
      notification.error({
        message: t('summary.msg.htmlFailTitle'),
        description: t('summary.msg.htmlFailDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const getFILE = async () => {
    setLoadingType('file');
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const queryString = `username=${encodeURIComponent(detailSummary.username)}&fileName=${encodeURIComponent(getFilename)}`;
      window.location.href = `${URL}${endpoints.GET_FILE_DOWNLOAD.url}?${queryString}`;
    } catch (error) {
      notification.error({
        message: t('summary.msg.downloadFailTitle'),
        description: t('summary.msg.fileFailDesc'),
        placement: 'top',
        duration: 3,
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  // 스크롤 이벤트 리스너 추가
  useEffect(() => {
    const handleScroll = () => {
      // 스크롤 위치가 300px 이상이면 버튼 표시
      setShowScrollTop(window.scrollY > 300);
    };
    
    // 이벤트 리스너 등록 및 해제
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // 상단으로 스크롤하는 함수
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // 특정 섹션으로 스크롤하는 함수
  // const scrollToSection = (sectionId) => {
  //   const section = document.getElementById(sectionId);
  //   if (section) {
  //     section.scrollIntoView({ behavior: 'smooth' });
  //   }
  // };

  return (
    <>
      <div id="pdf-content" className={`summary-wapper 
        ${isPopupWindow ? 'popup-mode' : ''} 
        ${isIframeView ? 'iframe-mode' : ''} 
        ${isStandalone ? 'standalone-mode' : ''}`}
      >
        {/* 독립 실행 모드나 팝업 모드일 때만 제목 표시 */}
        {(isStandalone || isPopupWindow) && (
          <div className="page-title p-16">
            {t('pageTitle.summary')}
            {(detailSummary.version === 'N/A' && detailSummary.distribution === 'N/A') && (
              <span className="unsupported-version">*Unsupported Version</span>
            )}
          </div>
        )}
        
        <div className="summary-header">
          <div className="info-section">
            <div className="info-item">
              <span className="info-label">Report Name</span>
              <span className="info-value">{getFilename}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Collection Date</span>
              <span className="info-value">{extractedDate}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Upload Date</span>
              <span className="info-value">{uploadDate}</span>
            </div>
          </div>
          
          {/* 독립 실행 모드나 팝업 모드일 때만 액션 버튼 표시 */}
          {/* {(isStandalone || isPopupWindow) && ( */}
            <div className="action-buttons">
              <Tooltip title={t('summary.tip.pdf')} overlayClassName="action-tooltip">
                <button className="action-button" onClick={generatePDF}>
                  <img src={PDF_1} alt="PDF" />
                </button>
              </Tooltip>
              <Tooltip title={t('summary.tip.html')} overlayClassName="action-tooltip">
                <button className="action-button" onClick={getHTML}>
                  <img src={HTML_1} alt="HTML" />
                </button>
              </Tooltip>
              <Tooltip title={t('summary.tip.excel')} overlayClassName="action-tooltip">
                <button className="action-button" onClick={getExcel}>
                  <img src={XLS_1} alt="Excel" />
                </button>
              </Tooltip>
              <Tooltip title={t('summary.tip.file')} overlayClassName="action-tooltip">
                <button className="action-button" onClick={getFILE}>
                  <img src={FILE} alt="File" />
                </button>
              </Tooltip>
            </div>
          {/* )} */}
        </div>
        
        <div className="summary-wapper__title fz-20">Summary Information</div>

        <div className="summary-wapper__down info" id="summary-info-section">
          {detailSummary &&
            Object.entries(detailSummary).map(([key, value]) => {
              const isStability = key === "stability";
              const stabilityValue = isStability ? parseFloat(value) : null;
              const stabilityMsg = detailSummary.stabilityMsg;
              const isSwapSizeZero =
                detailSummary.swapSize &&
                detailSummary.swapSize.trim() === "0 B";

              if (isSwapSizeZero && (key === "swapFree" || key === "swapCache")) {
                return null;
              }

              return (
                key !== "filename" &&
                key !== "diskUsage" &&
                key !== "partitionInfo" &&
                key !== "networkIP" &&
                key !== "networkTraffic" &&
                key !== "networkErrTraffic" &&
                key !== "packageList" &&
                key !== "orgFilename" &&
                key !== "stabilityMsg" &&
                key !== "extractedDate" &&
                key !== "insDate" &&
                key !== "username" &&
                key !== "routeInfo"
                ? (
                  isStability && stabilityValue < 100 && stabilityMsg ? (
                    <Tooltip title={renderTooltipContent(stabilityMsg)} key={key}>
                      <div
                        style={{
                          background: UtilRenderColor(value)
                        }}
                        className="flex flex-col gap-15 summary-wapper__detail"
                      >
                        <div className="fz-15 summary-wapper__detail--text1">
                          {t(`summary.field.${key}`, { defaultValue: keyMapping[key] || key })}
                        </div>
                        <div className="fz-30 summary-wapper__detail--text2">
                          {(value ?? 'N/A').toString().trim()}
                        </div>
                      </div>
                    </Tooltip>
                  ) : (
                    <div
                      key={key}
                      style={{
                        background: isStability ? UtilRenderColor(value) : randomColors[key]
                      }}
                      className="flex flex-col gap-15 summary-wapper__detail"
                    >
                      <div className="fz-15 summary-wapper__detail--text1">
                        {t(`summary.field.${key}`, { defaultValue: keyMapping[key] || key })}
                      </div>
                      <div className="fz-30 summary-wapper__detail--text2">
                        {(value ?? 'N/A').toString().trim()}
                      </div>
                    </div>
                  )
                )
                : null
              );
          })}
        </div>
        <div className="summary-wapper__down" id="disk-usage-section">
          <DiskUsage diskUsage={diskUsage} />
        </div>
        <div className="summary-wapper__down" id="partition-info-section">
          <PartitionInfo part={part} />
        </div>
        <div className="summary-wapper__down" id="network-ip-section">
          <NetworkIP networkIp={networkIP} />
        </div>
        {detailSummary.routeInfo && detailSummary.routeInfo !== 'N/A' ? (
          <div className="summary-wapper__down" id="route-info-section">
            <RoutingPage routeInfo={detailSummary.routeInfo} />
          </div>
        ) : null}
        {Array.isArray(network_traffic) && network_traffic[0] !== 'N/A' ? (
          <div className="summary-wapper__down" id="network-traffic-section">
            <NetworkTraffic header={ntheaders} networkTraffic={network_traffic} />
          </div>
        ) : null}
        {Array.isArray(network_err_traffic) && network_err_traffic[0] !== 'N/A' ? (
          <div className="summary-wapper__down" id="network-error-traffic-section">
            <NetworkErrTraffic header={netheaders} networkErrTraffic={network_err_traffic} />
          </div>
        ) : null}
        {detailSummary.stabilityMsg && detailSummary.stabilityMsg !== 'N/A' ? (
          <div className="summary-wapper__down" id="stability-section">
            <StabilityInfo stabilityMsg={detailSummary.stabilityMsg} />
          </div>
        ) : null}
      </div>
      <Modal
        open={isLoading}
        footer={null}
        closable={false}
        centered
        width={300}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '10px' }}>
            {loadingType === 'pdf' && t('summary.loading.pdf')}
            {loadingType === 'excel' && t('summary.loading.excel')}
            {loadingType === 'html' && t('summary.loading.html')}
            {loadingType === 'file' && t('summary.loading.file')}
          </p>
        </div>
      </Modal>
      <Modal
        open={pageLoading}
        footer={null}
        closable={false}
        centered
        width={300}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '10px' }}>{t('summary.loading.page')}</p>
        </div>
      </Modal>
      {/* 고정된 맨 위로 버튼 */}
      {showScrollTop && (
        <button 
          className="scroll-to-top-button"
          onClick={scrollToTop}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default Summary;
